import { useEffect, useState, type ReactNode } from 'react';
import {
  MAX_MODEL_SIZE_BYTES,
  type EvaluationRun,
  type Model,
  type ModelUpload,
  type Video,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import TextArea from '@carbon/react/es/components/TextArea/TextArea';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import Modal from '@carbon/react/es/components/Modal/Modal';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import FolderAdd from '@carbon/icons-react/es/FolderAdd';

import { apiFetch } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { AddToProjectModal } from '../features/projects/AddToProjectModal.js';
import {
  ModelSectionNav,
  sectionFromHash,
  type ModelSection,
} from '../features/model/ModelSectionNav.js';
import { ModelLibrarySection } from '../features/model/ModelLibrarySection.js';
import { ModelProcessBlock } from '../features/model/ModelProcessBlock.js';
import { PageHeader } from '../components/PageHeader.js';
import { HelpHint } from '../components/HelpHint.js';

/** Résumé de métriques rangé sur le modèle après une évaluation (worker, ADR-0014). */
interface MetricsSummary {
  mAP?: number;
  precision?: number;
  recall?: number;
  f1?: number;
  imageCount?: number;
}

/** Un ratio `[0,1]` en pourcentage entier, ou « — » s'il est absent. */
const pct = (value: number | undefined): string =>
  typeof value === 'number' ? `${Math.round(value * 100)}%` : '—';

/**
 * Registre de modèles (ADR-0014, Phase B). Importer un `.onnx` (via URL pré-signée : l'octet ne
 * transite pas par l'API) et lister les modèles de l'organisation. L'évaluation viendra se greffer
 * ici (Phase A).
 */
export function ModelsPage() {
  const t = useT();
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [version, setVersion] = useState('1');
  const [description, setDescription] = useState('');
  const [classes, setClasses] = useState('');
  const [inputSize, setInputSize] = useState(640);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [evaluating, setEvaluating] = useState<Model | null>(null);
  const [addingToProject, setAddingToProject] = useState<Model | null>(null);
  // Historique des évaluations : modèle déplié + cache par modèle (chargé à la demande).
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [evaluations, setEvaluations] = useState<Record<string, EvaluationRun[]>>({});

  // Section active, ancrée dans le fragment d'URL (ADR-0017) : partageable et stable au rechargement.
  const [section, setSection] = useState<ModelSection>(() => sectionFromHash(window.location.hash));
  useEffect(() => {
    const onHashChange = () => setSection(sectionFromHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const refresh = async () => {
    const list = await apiFetch<Model[]>('/models');
    setModels(list);
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [list, videoList] = await Promise.all([
          apiFetch<Model[]>('/models'),
          apiFetch<Video[]>('/videos'),
        ]);
        if (active) {
          setModels(list);
          setVideos(videoList);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : t('model.error.load'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // Chargement au montage uniquement : `t` n'y sert que de message de repli et ne doit pas
    // redéclencher la requête à chaque changement de langue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Déplie l'historique d'un modèle et charge ses évaluations une seule fois.
  const toggleHistory = async (modelId: string) => {
    if (historyFor === modelId) {
      setHistoryFor(null);
      return;
    }
    setHistoryFor(modelId);
    if (!evaluations[modelId]) {
      try {
        const runs = await apiFetch<EvaluationRun[]>(`/models/${modelId}/evaluations`);
        setEvaluations((prev) => ({ ...prev, [modelId]: runs }));
      } catch (err) {
        setActionError(err instanceof Error ? err.message : t('model.error.historyUnavailable'));
      }
    }
  };

  const onLaunchEvaluation = async (modelId: string, videoId: string) => {
    setActionError(null);
    try {
      await apiFetch<EvaluationRun>(`/models/${modelId}/evaluate`, {
        method: 'POST',
        body: { videoId },
      });
      setEvaluating(null);
      // La course tourne en tâche de fond ; on rafraîchit pour voir apparaître les métriques.
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('model.error.evaluate'));
    }
  };

  const onImport = async () => {
    if (!file || !name.trim() || !version.trim()) {
      setActionError(t('model.error.required'));
      return;
    }
    if (file.size > MAX_MODEL_SIZE_BYTES) {
      setActionError(t('model.error.tooLarge'));
      return;
    }
    setActionError(null);
    setBusy(true);
    try {
      const classNames = classes
        .split(/[\n,]/)
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      // 1) Enregistre les métadonnées, obtient l'URL de dépôt pré-signée.
      const { model, uploadUrl } = await apiFetch<ModelUpload>('/models', {
        method: 'POST',
        body: {
          name: name.trim(),
          version: version.trim(),
          description: description.trim() || undefined,
          classNames,
          inputSize,
          filename: file.name,
          sizeBytes: file.size,
          contentType: file.type || 'application/octet-stream',
        },
      });

      // 2) Dépôt direct vers le stockage : pré-signé, sans jeton ni cookie (ils casseraient la signature).
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!put.ok) throw new Error(t('model.error.uploadRefused'));

      // 3) Confirme : le modèle devient READY.
      await apiFetch(`/models/${model.id}/confirm`, { method: 'POST' });

      setName('');
      setVersion('1');
      setDescription('');
      setClasses('');
      setFile(null);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('model.error.import'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (modelId: string) => {
    setActionError(null);
    try {
      await apiFetch(`/models/${modelId}`, { method: 'DELETE' });
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('model.error.delete'));
    }
  };

  const videoTitleById = new Map(videos.map((video) => [video.id, video.title]));
  // Comparaison : modèles évalués, du meilleur mAP au moins bon.
  const ranked = [...models]
    .map((model) => ({ model, summary: model.metrics as MetricsSummary | null }))
    .filter((entry) => typeof entry.summary?.mAP === 'number')
    .sort((a, b) => (b.summary?.mAP ?? 0) - (a.summary?.mAP ?? 0));

  return (
    <section className="ddp-page__section ddp-models">
      <PageHeader title={t('model.title')} lead={t('model.intro')} tourScope="model" />

      {/* Bloc processus « Créer un modèle » : point d'entrée en tête de page (ADR-0028). */}
      <ModelProcessBlock />

      <ModelSectionNav active={section} />

      {error && (
        <InlineNotification
          kind="error"
          title={t('model.notif.errorTitle')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
      {actionError && (
        <InlineNotification
          kind="error"
          title={t('model.notif.actionErrorTitle')}
          subtitle={actionError}
          lowContrast
          hideCloseButton
        />
      )}

      {/* « Construire un modèle » : import d'un poids .onnx (l'octet va direct au stockage). */}
      {section === 'build' && (
        <>
          <div className="ddp-models__import">
            <h2 id="model-build" className="ddp-models__subtitle">
              {t('model.import.title')}
              <HelpHint textKey="help.model.import" />
            </h2>
            <div className="ddp-models__form">
              <TextInput
                id="model-name"
                labelText={t('model.field.name')}
                placeholder={t('model.field.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextInput
                id="model-version"
                labelText={t('model.field.version')}
                placeholder={t('model.field.versionPlaceholder')}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
              <TextInput
                id="model-input"
                labelText={t('model.field.inputSize')}
                type="number"
                value={String(inputSize)}
                onChange={(e) => setInputSize(Number(e.target.value) || 640)}
              />
            </div>
            <TextArea
              id="model-classes"
              labelText={t('model.field.classes')}
              helperText={t('model.field.classesHelper')}
              rows={3}
              value={classes}
              onChange={(e) => setClasses(e.target.value)}
            />
            <TextInput
              id="model-desc"
              labelText={t('model.field.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <label className="ddp-models__file">
              <span>{t('model.field.artifact')}</span>
              <input
                type="file"
                accept=".onnx,application/octet-stream"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <Button kind="primary" size="sm" disabled={busy} onClick={() => void onImport()}>
              {busy ? t('model.import.busy') : t('model.import.submit')}
            </Button>
          </div>
        </>
      )}

      {/* « Model Zoo » : vos modèles en haut, catalogue pré-entraîné (déjà entraînés) en dessous. */}
      {section === 'modelZoo' &&
        (loading ? (
          <Loading withOverlay={false} description={t('model.loading')} />
        ) : (
          <>
            <h2 id="model-zoo-mine" className="ddp-models__subtitle">
              {t('model.zoo.mine')} ({models.length})
              <HelpHint textKey="help.model.registry" />
            </h2>

            {ranked.length >= 2 && (
              <>
                <h2 className="ddp-models__subtitle">
                  {t('model.compare.title')} ({ranked.length} {t('model.compare.evaluatedSuffix')})
                </h2>
                <div className="ddp-table-scroll">
                  <table className="ddp-compare">
                    <thead>
                      <tr>
                        <th>{t('model.compare.col.model')}</th>
                        <th>mAP</th>
                        <th>{t('model.compare.col.precision')}</th>
                        <th>{t('model.compare.col.recall')}</th>
                        <th>F1</th>
                        <th>{t('model.compare.col.images')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked.map(({ model, summary }, index) => (
                        <tr
                          key={model.id}
                          className={index === 0 ? 'ddp-compare__best' : undefined}
                        >
                          <td>
                            {model.name} · v{model.version}
                          </td>
                          <td>{pct(summary?.mAP)}</td>
                          <td>{pct(summary?.precision)}</td>
                          <td>{pct(summary?.recall)}</td>
                          <td>{pct(summary?.f1)}</td>
                          <td>{summary?.imageCount ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="ddp-models__hint">{t('model.compare.hint')}</p>
              </>
            )}

            {models.length === 0 ? (
              <div className="ddp-empty">
                <p className="ddp-empty__title">{t('model.yours.empty.title')}</p>
                <p className="ddp-empty__body">{t('model.yours.empty.body')}</p>
              </div>
            ) : (
              <ul className="ddp-models__list">
                {models.map((model) => (
                  <li key={model.id} className="ddp-models__item">
                    <div className="ddp-models__item-main">
                      <span className="ddp-models__item-name">
                        {model.name} · v{model.version}
                      </span>
                      <span
                        className={`ddp-models__badge ddp-models__badge--${model.status.toLowerCase()}`}
                      >
                        {model.status === 'READY'
                          ? t('model.status.ready')
                          : model.status === 'PENDING'
                            ? t('model.status.pending')
                            : t('model.status.failed')}
                      </span>
                    </div>
                    <div className="ddp-models__item-meta">
                      <span>{model.task}</span>
                      <span>{model.format}</span>
                      <span>
                        {model.classNames.length} {t('model.classesCountSuffix')}
                      </span>
                      <span>{model.inputSize}px</span>
                      <span>{formatBytes(model.sizeBytes)}</span>
                      <span>
                        {model.source === 'TRAINED'
                          ? t('model.source.trained')
                          : t('model.source.imported')}
                      </span>
                      {model.createdByName && (
                        <span>
                          {t('model.byPrefix')} {model.createdByName}
                        </span>
                      )}
                      {renderMetrics(model.metrics)}
                    </div>
                    <div className="ddp-models__item-actions">
                      {model.status === 'READY' && (
                        <Button
                          kind="tertiary"
                          size="sm"
                          disabled={videos.length === 0}
                          onClick={() => setEvaluating(model)}
                        >
                          {t('model.action.evaluate')}
                        </Button>
                      )}
                      <Button
                        kind="tertiary"
                        size="sm"
                        renderIcon={FolderAdd}
                        onClick={() => setAddingToProject(model)}
                      >
                        {t('data.addToProject.add')}
                      </Button>
                      <Button kind="ghost" size="sm" onClick={() => void toggleHistory(model.id)}>
                        {historyFor === model.id
                          ? t('model.action.hide')
                          : t('model.action.history')}
                      </Button>
                      <Button
                        kind="danger--ghost"
                        size="sm"
                        onClick={() => void onDelete(model.id)}
                      >
                        {t('model.action.delete')}
                      </Button>
                    </div>
                    {historyFor === model.id && (
                      <EvaluationHistory
                        runs={evaluations[model.id]}
                        videoTitleById={videoTitleById}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}

            <h2 id="model-zoo-catalog" className="ddp-models__subtitle">
              {t('model.zoo.catalog')}
              <HelpHint textKey="help.model.catalog" />
            </h2>
            <p className="ddp-models__hint">{t('model.zoo.catalogHint')}</p>
            <ModelLibrarySection />
          </>
        ))}

      {evaluating && (
        <EvaluateModal
          model={evaluating}
          videos={videos}
          onClose={() => setEvaluating(null)}
          onLaunch={onLaunchEvaluation}
        />
      )}

      {addingToProject && (
        <AddToProjectModal
          target={{
            kind: 'model',
            ids: [addingToProject.id],
            label: `${addingToProject.name} · v${addingToProject.version}`,
          }}
          onClose={() => setAddingToProject(null)}
        />
      )}
    </section>
  );
}

/** Choix d'une vidéo puis lancement de l'évaluation (ADR-0014, Phase A). */
function EvaluateModal({
  model,
  videos,
  onClose,
  onLaunch,
}: {
  model: Model;
  videos: Video[];
  onClose: () => void;
  onLaunch: (modelId: string, videoId: string) => Promise<void>;
}) {
  const t = useT();
  const [videoId, setVideoId] = useState(videos[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!videoId) return;
    setBusy(true);
    await onLaunch(model.id, videoId);
    setBusy(false);
  };

  return (
    <Modal
      open
      modalHeading={`${t('model.eval.headingPrefix')} ${model.name} · v${model.version}`}
      primaryButtonText={busy ? t('model.eval.busy') : t('model.eval.submit')}
      secondaryButtonText={t('model.eval.cancel')}
      primaryButtonDisabled={busy || !videoId}
      onRequestClose={onClose}
      onRequestSubmit={() => void onSubmit()}
    >
      <p className="ddp-models__intro">{t('model.eval.intro')}</p>
      <Select
        id="eval-video"
        labelText={t('model.eval.videoLabel')}
        value={videoId}
        onChange={(e) => setVideoId(e.target.value)}
      >
        {videos.map((video) => (
          <SelectItem key={video.id} value={video.id} text={video.title} />
        ))}
      </Select>
    </Modal>
  );
}

/** Affiche le résumé de métriques d'un modèle, s'il a déjà été évalué. */
function renderMetrics(metrics: Model['metrics']): ReactNode {
  if (!metrics) return null;
  const m = metrics as MetricsSummary;
  if (typeof m.mAP !== 'number') return null;
  return (
    <span className="ddp-models__metrics">
      mAP {pct(m.mAP)} · P {pct(m.precision)} · R {pct(m.recall)}
    </span>
  );
}

/** Historique des évaluations d'un modèle : une course par ligne, avec le détail par classe. */
function EvaluationHistory({
  runs,
  videoTitleById,
}: {
  runs: EvaluationRun[] | undefined;
  videoTitleById: Map<string, string>;
}) {
  const t = useT();
  if (!runs) return <p className="ddp-models__hint">{t('model.history.loading')}</p>;
  if (runs.length === 0) return <p className="ddp-models__hint">{t('model.history.empty')}</p>;

  const statusLabel: Record<EvaluationRun['status'], string> = {
    PENDING: t('model.history.status.pending'),
    RUNNING: t('model.history.status.running'),
    DONE: t('model.history.status.done'),
    FAILED: t('model.history.status.failed'),
  };

  return (
    <div className="ddp-history">
      {runs.map((run) => (
        <div key={run.id} className="ddp-history__run">
          <div className="ddp-history__head">
            <span className={`ddp-models__badge ddp-models__badge--${run.status.toLowerCase()}`}>
              {statusLabel[run.status]}
            </span>
            <span className="ddp-history__video">
              {videoTitleById.get(run.scopeId) ?? t('model.history.videoFallback')}
            </span>
            <span className="ddp-overview__muted">{formatDateTime(run.createdAt)}</span>
            {run.metrics && (
              <span className="ddp-models__metrics">
                mAP {pct(run.metrics.mAP)} · P {pct(run.metrics.precision)} · R{' '}
                {pct(run.metrics.recall)} · F1 {pct(run.metrics.f1)}
              </span>
            )}
          </div>
          {run.status === 'FAILED' && run.failureReason && (
            <p className="ddp-history__error">{run.failureReason}</p>
          )}
          {run.metrics && run.metrics.perClass.length > 0 && (
            <div className="ddp-table-scroll">
              <table className="ddp-compare ddp-compare--small">
                <thead>
                  <tr>
                    <th>{t('model.history.col.class')}</th>
                    <th>AP</th>
                    <th>P</th>
                    <th>R</th>
                    <th>{t('model.history.col.gt')}</th>
                    <th>{t('model.history.col.confusion')}</th>
                  </tr>
                </thead>
                <tbody>
                  {run.metrics.perClass.map((c) => (
                    <tr key={c.label}>
                      <td>{c.label}</td>
                      <td>{pct(c.ap)}</td>
                      <td>{pct(c.precision)}</td>
                      <td>{pct(c.recall)}</td>
                      <td>{c.gtCount}</td>
                      <td>
                        {c.tp}/{c.fp}/{c.fn}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatDateTime(value: Date | string): string {
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
